import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Card, Input, Space, Typography, message } from "antd";
import { useAuth } from "contexts/AuthContext";
import AuthShell from "components/auth/AuthShell";
import AuthBrand from "components/auth/AuthBrand";
import paths from "routes/paths";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { Title, Text, Paragraph } = Typography;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LINKED_ACCOUNT_LOGIN_KEY = "olreadiaLinkedAccountLogin";

const socialSetupStyles = `
  .olreadia-social-setup-card {
    border-radius: 28px;
    background: linear-gradient(155deg, rgba(255,255,255,0.98) 0%, rgba(246,255,249,0.96) 58%, rgba(255,253,235,0.94) 100%);
    box-shadow: 0 26px 72px rgba(0, 82, 64, 0.14), inset 0 1px 0 rgba(255,255,255,0.95);
    border: 1px solid ${OLIVEEB_COLORS.oliveebGreenSoft};
    overflow: hidden;
  }
  .olreadia-social-setup-input {
    height: 54px;
    border-radius: 999px !important;
    border-color: ${OLIVEEB_COLORS.oliveebGreenSoft} !important;
    font-size: 17px;
    font-weight: 800;
    color: ${OLIVEEB_COLORS.oliveebDarkBlue};
    box-shadow: 0 14px 28px rgba(16,147,87,0.10);
  }
  .olreadia-social-setup-input.ant-input-affix-wrapper,
  .olreadia-social-setup-input.ant-input-password {
    border-radius: 999px !important;
    overflow: hidden;
  }
  .olreadia-social-setup-input .ant-input {
    font-size: 17px;
    font-weight: 800;
    color: ${OLIVEEB_COLORS.oliveebDarkBlue};
  }
  .olreadia-social-setup-input:focus,
  .olreadia-social-setup-input-focused {
    border-color: ${OLIVEEB_COLORS.oliveebGreen} !important;
    box-shadow: 0 0 0 4px rgba(39,174,48,0.12), 0 14px 28px rgba(16,147,87,0.10) !important;
  }
`;

const primaryActionStyle = {
  height: 50,
  borderRadius: 999,
  fontWeight: 900,
  fontSize: 16,
  background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreen} 0%, ${OLIVEEB_COLORS.oliveebBlue} 100%)`,
  borderColor: OLIVEEB_COLORS.oliveebGreen,
  boxShadow: "0 12px 24px rgba(16,147,87,0.22)",
};

const secondaryActionStyle = {
  height: 42,
  borderRadius: 999,
  fontWeight: 900,
  color: OLIVEEB_COLORS.oliveebBlue,
  borderColor: OLIVEEB_COLORS.oliveebBlue,
  background: OLIVEEB_COLORS.white,
  boxShadow: "0 10px 22px rgba(0,87,184,0.12)",
};

const parseDisplayName = (name = "") => {
  const trimmed = String(name || "").trim();
  if (!trimmed) return { firstName: "", lastName: "" };

  const cleaned = trimmed.replace(/\s+Apple$/i, "").trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: parts[0] || "", lastName: "" };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

const validatePassword = (password) => {
  if (password.length < 8) return "Password must be at least 8 characters long";
  if (!/(?=.*[a-z])/.test(password)) return "Password must contain at least one lowercase letter";
  if (!/(?=.*[A-Z])/.test(password)) return "Password must contain at least one uppercase letter";
  if (!/(?=.*\d)/.test(password)) return "Password must contain at least one number";
  return null;
};

const providerLabel = (provider) =>
  ({
    apple: "Apple",
    google: "Google",
    facebook: "Facebook",
    microsoft: "Microsoft",
    linkedin: "LinkedIn",
  })[provider] || "Social";

const getIdentityToken = (appleResponse = {}, identityToken = "") =>
  identityToken ||
  appleResponse?.authorization?.id_token ||
  appleResponse?.authorization?.idToken ||
  appleResponse?.identityToken ||
  appleResponse?.id_token ||
  appleResponse?.idToken ||
  "";

const getAppleUserId = (appleResponse = {}, fallback = "") =>
  fallback ||
  (typeof appleResponse?.user === "string" ? appleResponse.user : "") ||
  appleResponse?.userId ||
  "";

const AuthAppleSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithApple, linkAppleAccount, linkSocialAccount, refreshAuth } = useAuth();

  const state = location.state || {};
  const provider = String(state.provider || "apple").toLowerCase();
  const providerName = providerLabel(provider);
  const authPayload = state.authPayload || {};
  const appleResponse = state.appleResponse || {};
  const identityToken = getIdentityToken(appleResponse, state.identityToken || authPayload.identityToken || authPayload.idToken);
  const appleUserId = getAppleUserId(appleResponse, state.appleUserId || authPayload.appleUserId);
  const providerUserId =
    state.providerUserId ||
    authPayload.providerUserId ||
    authPayload.userId ||
    authPayload.googleUserId ||
    authPayload.appleUserId ||
    authPayload.microsoftUserId ||
    authPayload.facebookUserId ||
    authPayload.linkedinUserId ||
    authPayload.user ||
    appleUserId;
  const userInfo = state.userInfo || {};

  const initialDisplayName =
    userInfo.name ||
    appleResponse.name ||
    [appleResponse.firstName, appleResponse.lastName].filter(Boolean).join(" ") ||
    "";

  const parsedName = useMemo(() => parseDisplayName(initialDisplayName), [initialDisplayName]);

  const [mode, setMode] = useState(state.mode || (state.requiresLinkApproval ? "link" : "setup"));
  const [linkStep, setLinkStep] = useState("confirm");
  const [email, setEmail] = useState(userInfo.email || appleResponse.email || "");
  const [firstName, setFirstName] = useState(parsedName.firstName);
  const [lastName, setLastName] = useState(parsedName.lastName);
  const [phone, setPhone] = useState(appleResponse.phone || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [linkingProcessId, setLinkingProcessId] = useState(state.linkingProcessId || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(state.message || "");

  useEffect(() => {
    if (!email && userInfo.email) setEmail(userInfo.email);
    if (!firstName && parsedName.firstName) setFirstName(parsedName.firstName);
    if (!lastName && parsedName.lastName) setLastName(parsedName.lastName);
  }, [email, firstName, lastName, parsedName.firstName, parsedName.lastName, userInfo.email]);

  const finishAuth = async ({ linkedAccount = false } = {}) => {
    if (linkedAccount && typeof window !== "undefined") {
      window.sessionStorage.setItem(LINKED_ACCOUNT_LOGIN_KEY, "1");
    }
    await refreshAuth();
    navigate(paths.pmDashboard, { replace: true });
  };

  const handleSetupSubmit = async (event) => {
    event.preventDefault();

    if (provider !== "apple" || !identityToken) {
      setError(`${providerName} setup state is missing. Please sign in again.`);
      return;
    }

    const trimmedEmail = email.trim();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedPhone = phone.trim();

    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const result = await loginWithApple({
        ...appleResponse,
        identityToken,
        authorization: appleResponse.authorization || {
          id_token: identityToken,
          code: appleResponse.authorizationCode,
        },
        id_token: appleResponse.id_token || identityToken,
        user: appleResponse.user ?? appleUserId,
        email: trimmedEmail,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        phone: trimmedPhone,
      }, password);

      if (result?.success) {
        await finishAuth({ linkedAccount: true });
        return;
      }

      if (result?.requiresLinkApproval) {
        setMode("link");
        setLinkStep("confirm");
        setError(result.error || "An account already exists with this email. Please confirm linking.");
        return;
      }

      if (result?.requiresPassword) {
        setError(result.error || "Apple sign-in needs additional setup.");
        return;
      }

      setError(result?.error || "Apple sign-in failed. Please try again.");
    } catch (err) {
      setError(err?.message || "Apple sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartLink = async () => {
    if (provider === "apple" && (!identityToken || !appleUserId)) {
      setError("Apple linking state is missing. Please sign in again.");
      return;
    }

    if (provider !== "apple" && !providerUserId && !Object.keys(authPayload || {}).length) {
      setError(`${providerName} linking state is missing. Please sign in again.`);
      return;
    }

    const trimmedEmail = email.trim();
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError("Please enter the email address tied to the existing account.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const linkPayload = {
        email: trimmedEmail,
        appleUserId,
        providerUserId,
        identityToken,
        authPayload,
        password: password || undefined,
      };
      const result = provider === "apple"
        ? await linkAppleAccount(linkPayload)
        : await linkSocialAccount(provider, linkPayload);

      if (result?.success) {
        await finishAuth({ linkedAccount: true });
        return;
      }

      if (result?.requiresVerificationCode) {
        setLinkingProcessId(result?.data?.linkingProcessId || result?.linkingProcessId || "");
        setLinkStep("code");
        message.info("We sent a verification code to confirm the link.");
        return;
      }

      if (result?.details?.requiresPassword || result?.requiresPassword) {
        setLinkStep("password");
        message.warning(result?.error || "Enter your password to continue.");
        return;
      }

      setError(result?.error || "Unable to start account linking.");
    } catch (err) {
      setError(err?.message || "Unable to start account linking.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    if (!password.trim()) {
      setError("Enter your password to continue.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const linkPayload = {
        email: email.trim(),
        appleUserId,
        providerUserId,
        identityToken,
        authPayload,
        password,
      };
      const result = provider === "apple"
        ? await linkAppleAccount(linkPayload)
        : await linkSocialAccount(provider, linkPayload);

      if (result?.requiresVerificationCode) {
        setLinkingProcessId(result?.data?.linkingProcessId || result?.linkingProcessId || "");
        setLinkStep("code");
        message.info("We sent a verification code to confirm the link.");
        return;
      }

      if (result?.success) {
        await finishAuth({ linkedAccount: true });
        return;
      }

      setError(result?.error || "Unable to verify your password.");
    } catch (err) {
      setError(err?.message || "Unable to verify your password.");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (event) => {
    event.preventDefault();

    if (verificationCode.trim().length !== 6) {
      setError("Please enter the 6-digit verification code.");
      return;
    }

    if (!linkingProcessId) {
      setError("Missing linking request. Please start the link step again.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const linkPayload = {
        email: email.trim(),
        appleUserId,
        providerUserId,
        identityToken,
        authPayload,
        password: password || undefined,
        verificationCode: verificationCode.trim(),
        linkingProcessId,
      };
      const result = provider === "apple"
        ? await linkAppleAccount(linkPayload)
        : await linkSocialAccount(provider, linkPayload);

      if (result?.success) {
        await finishAuth();
        return;
      }

      setError(result?.error || "Unable to verify the confirmation code.");
    } catch (err) {
      setError(err?.message || "Unable to verify the confirmation code.");
    } finally {
      setLoading(false);
    }
  };

  const isSetupMode = mode === "setup";
  const isLinkConfirm = mode === "link" && linkStep === "confirm";
  const isLinkPassword = mode === "link" && linkStep === "password";
  const isLinkCode = mode === "link" && linkStep === "code";

  return (
    <AuthShell mode="center">
      <style>{socialSetupStyles}</style>
      <div style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <Card
          variant="borderless"
          className="olreadia-social-setup-card"
          style={{
            width: "100%",
            maxWidth: 620,
          }}
          styles={{ body: { padding: 24 } }}
        >
          <Space direction="vertical" size={18} style={{ width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <Button
                onClick={() => navigate(paths.simpleLogin)}
                shape="circle"
                icon={<ArrowLeftOutlined />}
                style={secondaryActionStyle}
              />
              <div
                style={{
                  padding: "7px 14px",
                  borderRadius: 24,
                  background: OLIVEEB_COLORS.white,
                  border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                  boxShadow: "0 12px 24px rgba(39,174,48,0.14)",
                }}
              >
                <AuthBrand centered size={78} />
              </div>
              <Button
                type="link"
                onClick={() => navigate(paths.simpleLogin)}
                style={{ color: OLIVEEB_COLORS.oliveebBlue, fontWeight: 900, paddingInline: 0 }}
              >
                Sign in
              </Button>
            </div>

            <Space direction="vertical" size={6} style={{ width: "100%", textAlign: "center" }}>
              <Text
                style={{
                  color: OLIVEEB_COLORS.oliveebBlue,
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                {providerName} sign-in
              </Text>
              <Title
                level={2}
                style={{
                  margin: 0,
                  color: OLIVEEB_COLORS.oliveebGreen,
                  fontWeight: 900,
                  lineHeight: 1.12,
                }}
              >
                {isSetupMode ? `Complete ${providerName} setup` : `Link your ${providerName} account`}
              </Title>
              <Paragraph
                style={{
                  margin: 0,
                  color: OLIVEEB_COLORS.oliveebBlue,
                  fontSize: 15,
                  lineHeight: 1.6,
                }}
              >
                {isSetupMode
                  ? `Finish creating your ${providerName} sign-in profile.`
                  : `Confirm how you want to connect this ${providerName} account.`}
              </Paragraph>
            </Space>

            {error ? (
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  padding: 14,
                  borderRadius: 18,
                  background: "linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 100%)",
                  color: OLIVEEB_COLORS.oliveebDarkRed,
                  border: `1px solid ${OLIVEEB_COLORS.goldBorder}`,
                  fontSize: 14,
                }}
              >
                <span style={{ fontWeight: 800 }}>!</span>
                <span>{error}</span>
              </div>
            ) : null}

            {isSetupMode ? (
                <form onSubmit={handleSetupSubmit}>
                  <Space direction="vertical" size={14} style={{ width: "100%" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 6 }}>
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 999,
                          overflow: "hidden",
                          background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreenSoft} 0%, ${OLIVEEB_COLORS.white} 100%)`,
                          display: "grid",
                          placeItems: "center",
                          fontWeight: 900,
                          color: OLIVEEB_COLORS.oliveebGreen,
                          boxShadow: "0 10px 22px rgba(16,147,87,0.14)",
                        }}
                      >
                        {initialDisplayName?.charAt(0)?.toUpperCase() || providerName.charAt(0)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 900, color: OLIVEEB_COLORS.oliveebDarkBlue }}>
                          {initialDisplayName || `${providerName} user`}
                        </div>
                        <div style={{ color: OLIVEEB_COLORS.oliveebBlue, fontSize: 13, fontWeight: 700 }}>
                          Enter your details to finish setup.
                        </div>
                      </div>
                    </div>

                    <Input
                      className="olreadia-social-setup-input"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      autoComplete="email"
                    />
                    <Input
                      className="olreadia-social-setup-input"
                      type="text"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={loading}
                      autoComplete="given-name"
                    />
                    <Input
                      className="olreadia-social-setup-input"
                      type="text"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={loading}
                      autoComplete="family-name"
                    />
                    <Input
                      className="olreadia-social-setup-input"
                      type="tel"
                      placeholder="Phone number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={loading}
                      autoComplete="tel"
                    />
                    <Input.Password
                      className="olreadia-social-setup-input"
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                    <Input.Password
                      className="olreadia-social-setup-input"
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading}
                      autoComplete="new-password"
                    />

                    <div style={{ display: "flex", gap: 12, justifyContent: "space-between", flexWrap: "wrap" }}>
                      <Button onClick={() => navigate(paths.simpleLogin)} disabled={loading} style={secondaryActionStyle}>
                        Cancel
                      </Button>
                      <Button type="primary" htmlType="submit" loading={loading} style={primaryActionStyle}>
                        Continue
                      </Button>
                    </div>
                  </Space>
                </form>
              ) : null}

              {isLinkConfirm ? (
                <Space direction="vertical" size={14} style={{ width: "100%" }}>
                  <div
                    style={{
                      padding: 16,
                      borderRadius: 20,
                      background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebBlueSoft} 0%, ${OLIVEEB_COLORS.white} 100%)`,
                      border: `1px solid ${OLIVEEB_COLORS.oliveebBlueSoft}`,
                      boxShadow: "0 12px 24px rgba(0,87,184,0.08)",
                    }}
                  >
                    <div style={{ fontWeight: 900, color: OLIVEEB_COLORS.oliveebDarkBlue, marginBottom: 6 }}>
                      {email || userInfo.email || "Existing account"}
                    </div>
                    <div style={{ color: OLIVEEB_COLORS.oliveebBlue, fontSize: 14, lineHeight: 1.55, marginBottom: 10 }}>
                      {state.message || `We found an existing account that matches this ${providerName} sign-in.`}
                    </div>
                    {/* <Text style={{ color: OLIVEEB_COLORS.textMuted, fontSize: 12, fontWeight: 800 }}>
                      This email comes from your existing account and cannot be changed here.
                    </Text> */}
                  </div>

                  <div style={{ display: "flex", gap: 12, justifyContent: "space-between", flexWrap: "wrap" }}>
                    <Button onClick={() => navigate(paths.simpleLogin)} disabled={loading} style={secondaryActionStyle}>
                      Deny
                    </Button>
                    <Button type="primary" onClick={handleStartLink} loading={loading} style={primaryActionStyle}>
                      Accept & Link
                    </Button>
                  </div>
                </Space>
              ) : null}

              {isLinkPassword ? (
                <form onSubmit={handlePasswordSubmit}>
                  <Space direction="vertical" size={14} style={{ width: "100%" }}>
                    <Input.Password
                      className="olreadia-social-setup-input"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      autoComplete="current-password"
                    />
                    <div style={{ display: "flex", gap: 12, justifyContent: "space-between", flexWrap: "wrap" }}>
                      <Button onClick={() => setLinkStep("confirm")} disabled={loading} style={secondaryActionStyle}>
                        Back
                      </Button>
                      <Button type="primary" htmlType="submit" loading={loading} style={primaryActionStyle}>
                        Continue
                      </Button>
                    </div>
                  </Space>
                </form>
              ) : null}

              {isLinkCode ? (
                <form onSubmit={handleCodeSubmit}>
                  <Space direction="vertical" size={14} style={{ width: "100%" }}>
                    <Input
                      className="olreadia-social-setup-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="123456"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/[^\d]/g, ""))}
                      disabled={loading}
                      autoComplete="one-time-code"
                    />
                    <div style={{ display: "flex", gap: 12, justifyContent: "space-between", flexWrap: "wrap" }}>
                      <Button onClick={() => setLinkStep("password")} disabled={loading} style={secondaryActionStyle}>
                        Back
                      </Button>
                      <Button type="primary" htmlType="submit" loading={loading} style={primaryActionStyle}>
                        Confirm link
                      </Button>
                    </div>
                  </Space>
                </form>
              ) : null}
            </Space>
        </Card>
      </div>
    </AuthShell>
  );
};

export default AuthAppleSetup;
